<template>
  <div
    style="aspect-ratio: 414 / 588"
    class="relative w-full overflow-hidden rounded-3xl bg-black"
  >
    <img
      ref="node"
      :src="slides[currentPage]"
      class="absolute top-0 h-full w-full"
      alt=""
    />

    <div class="relative left-[2%] inline-flex w-[96%] space-x-2">
      <ProgressRoot
        v-for="(_, index) in slides"
        :key="index"
        v-model="progress"
        class="relative inline-flex h-1 overflow-hidden rounded-full bg-zinc-400"
        :style="{ width: `${100 / slides.length}%` }"
      >
        <ProgressIndicator
          class=""
          :class="'h-full w-full rounded-full bg-white'"
          :style="
            index < currentPage
              ? `transform: translateX(0)`
              : index > currentPage
                ? `transform: translateX(-100%)`
                : `transform: translateX(-${100 - progress}%)`
          "
        />
      </ProgressRoot>
    </div>

    <button
      type="button"
      class="absolute left-0 top-0 z-10 h-full w-1/3 outline-none"
      @click="previousPage"
    >
      <span class="sr-only">Previous</span>
    </button>
    <button
      type="button"
      class="absolute right-0 top-0 z-10 h-full w-2/3 outline-none"
      @click="nextPage"
    >
      <span class="sr-only">Next</span>
    </button>
    <div class="absolute right-2 top-[4%] z-10 w-fit space-x-2 text-white">
      <button @click="toggleNavigating">
        <svg
          v-if="!isPlaying"
          xmlns="http://www.w3.org/2000/svg"
          width="30"
          height="30"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M18.4 12.5L9 18.38L8 19V6zm-1.9 0L9 7.8v9.4z"
          />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          width="30"
          height="30"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M13.5 18V6H18v12zM6 18V6h4.5v12zm8.5-1H17V7h-2.5zM7 17h2.5V7H7zM7 7v10zm7.5 0v10z"
          />
        </svg>
      </button>
      <button @click="toggleMusic">
        <svg
          v-if="!isMuted"
          width="30"
          height="31"
          viewBox="0 0 30 31"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.21875 19.2031V11.7031H7.5L14.5312 7.48438V23.4219L7.5 19.2031H4.21875Z"
            stroke="white"
          />
          <path
            d="M17.5572 9.66511C18.5937 10.207 19.4657 11.0173 20.0821 12.0114C20.6985 13.0055 21.0366 14.1469 21.0611 15.3163C21.0856 16.4858 20.7956 17.6403 20.2214 18.6593C19.6472 19.6784 18.8098 20.5245 17.7969 21.1094"
            stroke="white"
          />
          <path
            d="M19.9219 24.79C21.594 23.8246 22.9762 22.4278 23.924 20.7457C24.8719 19.0635 25.3506 17.1577 25.3101 15.2273C25.2697 13.297 24.7116 11.4129 23.6942 9.77188C22.6767 8.13091 21.2372 6.79328 19.5262 5.89874"
            stroke="white"
          />
        </svg>
        <svg
          v-else
          width="30"
          height="31"
          viewBox="0 0 30 31"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.21875 19.2031V11.7031H7.5L14.5312 7.48438V23.4219L7.5 19.2031H4.21875Z"
            stroke="white"
          />
          <path d="m18 17l2-2m2-2l-2 2m0 0l-2-2m2 2l2 2" stroke="white" />
        </svg>
      </button>
      <DialogClose aria-label="Close" as-child>
        <button @click="stopNavigation">
          <svg
            width="30"
            height="31"
            viewBox="0 0 30 31"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M7.5 7.95312L22.5 22.9531" stroke="white" />
            <path d="M22.5 7.95312L7.5 22.9531" stroke="white" />
          </svg>
        </button>
      </DialogClose>
    </div>
  </div>
  <div class="relative mt-2 flex max-w-full items-center justify-center">
    <button
      v-if="$device.isDesktop"
      class="relative inline-flex items-center justify-center space-x-4 rounded px-4 py-2 font-semibold leading-none text-white focus:outline-none"
      @click="downloadPage"
    >
      <svg
        width="32"
        height="33"
        viewBox="0 0 32 33"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="16" cy="16.4531" r="16" fill="white" />
        <path
          d="M18.0001 14.3698H20.5001V22.3698H11.5001V14.3698H14.0001"
          stroke="black"
          stroke-width="0.833333"
        />
        <path
          d="M16 17.8698V9.36981M16 9.36981L18.5 11.8698M16 9.36981L13.5 11.8698"
          stroke="black"
          stroke-width="0.833333"
          stroke-linejoin="bevel"
        />
      </svg>
      <p>Download this slide</p>
    </button>
    <button
      v-if="$device.isMobile"
      class="relative inline-flex items-center justify-center space-x-4 rounded px-4 py-2 font-semibold leading-none text-white focus:outline-none"
      @click="sharePage"
    >
      <svg
        width="32"
        height="33"
        viewBox="0 0 32 33"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="16" cy="16.4531" r="16" fill="white" />
        <path
          d="M18.0001 14.3698H20.5001V22.3698H11.5001V14.3698H14.0001"
          stroke="black"
          stroke-width="0.833333"
        />
        <path
          d="M16 17.8698V9.36981M16 9.36981L18.5 11.8698M16 9.36981L13.5 11.8698"
          stroke="black"
          stroke-width="0.833333"
          stroke-linejoin="bevel"
        />
      </svg>
      <p>Share this slide</p>
    </button>
  </div>
</template>
<script setup lang="ts">
const props = defineProps<{
  slides: string[];
}>();

const progress = ref(0);
const isPlaying = ref(true);
const isMuted = ref(false);
const elapsedTime = ref(0);
const pausedElapsedTime = ref(0);
const startTime = ref(0);
const currentPage = ref(0);

let animationFrameId: number;

const isMounted = ref(false);
onMounted(() => {
  isMounted.value = true;
});

onUnmounted(() => {
  isMounted.value = false;
});

const audio = useSound("/sounds/wrapped.mp3", {
  async onload() {
    await nextTick();
    if (!isMounted.value) return;
    if (!isMuted.value && !audio.isPlaying.value && isPlaying.value) {
      audio.play();
    }
  },
});

const clearVariables = () => {
  startTime.value = 0;
  elapsedTime.value = 0;
  progress.value = 0;
};

const pageNavigation = () => {
  clearVariables();

  if (!isMuted.value && !audio.isPlaying.value && isMounted.value) {
    audio.play();
  }
  animationFrameId = requestAnimationFrame(animate);
};

function startNavigation() {
  isPlaying.value = true;
  pageNavigation();
}

const stopNavigation = () => {
  cancelAnimationFrame(animationFrameId);
  audio.stop();
  useWrappedUp().isOpen = false;
};

onMounted(() => {
  startNavigation();
});

onUnmounted(() => {
  stopNavigation();
});

function animate(timestamp: number) {
  if (startTime.value == 0) {
    startTime.value = timestamp;
  }
  elapsedTime.value = timestamp - startTime.value + pausedElapsedTime.value;
  progress.value = Math.round((elapsedTime.value * 100) / 5000);
  if (progress.value < 100 && isPlaying.value) {
    animationFrameId = requestAnimationFrame(animate);
  } else if (progress.value >= 100 && isPlaying.value) {
    pausedElapsedTime.value = 0;
    cancelAnimationFrame(animationFrameId);
    nextPage();
  } else if (!isPlaying.value) {
    cancelAnimationFrame(animationFrameId);
    pausedElapsedTime.value = elapsedTime.value;
  }
}

const previousPage = () => {
  if (currentPage.value > 0) {
    currentPage.value -= 1;
  }
  cancelAnimationFrame(animationFrameId);
  pausedElapsedTime.value = 0;
  pageNavigation();
};

const nextPage = async () => {
  if (currentPage.value < props.slides.length - 1) {
    currentPage.value += 1;
    isPlaying.value = true;
    cancelAnimationFrame(animationFrameId);
    clearVariables();
    pausedElapsedTime.value = 0;
    pageNavigation();
  } else {
    stopNavigation();
  }
};

const toggleMusic = () => {
  isMuted.value = !isMuted.value;
  if (!isMuted.value && isPlaying.value) {
    audio.play();
  } else {
    audio.pause();
  }
};

const toggleNavigating = () => {
  isPlaying.value = !isPlaying.value;
  if (isPlaying.value) {
    if (!isMuted.value) {
      audio.play();
    }
    clearVariables();
    pageNavigation();
  } else {
    audio.pause();
  }
};

const node = ref<HTMLImageElement>();
const sharePage = async () => {
  if (!node.value) {
    console.error("Error while sharing");
    return;
  }

  try {
    const dataUrl = node.value.src;
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File(
      [blob],
      `Wrapped-${currentPage.value}-${new Date().getTime()}.jpeg`,
      {
        type: "image/jpeg",
      },
    );

    const shareData = {
      files: [file],
    };

    if (navigator.canShare && navigator.canShare(shareData)) {
      toggleNavigating();
      await navigator.share(shareData).finally(() => {
        toggleNavigating();
      });
    } else {
      downloadPage();
    }
  } catch (error) {
    console.error("Error while sharing.");
  }
};

const downloadPage = async () => {
  try {
    if (!node.value) {
      console.error("Error while downloading");
      return;
    }
    const dataUrl = node.value.src;
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `Wrapped-${currentPage.value}-${new Date().getTime()}.jpeg`;
    link.click();
    link.remove();
  } catch (error) {
    console.error("Error while sharing.");
  }
};
</script>
